/* eslint-disable no-nested-ternary */
import { useState, FC, useEffect } from 'react';
import styled from '@emotion/styled';
import { useRouter } from 'next/router';
import { AnyArgVoidReturnFunction } from 'types/utils';
import boxShadowGenerator from '@utils/boxShadowGenerator';
import { segment } from '@actions/analytics';
import useCheckoutEventProps from '@hooks/useCheckoutEventProps';
import { Spacings, mediaQuery } from '@styles/index';
import TTUBanner from '@cont/TalkToUnacademy/Banner';
import useTTUStore from '@stores/ttu';
import { noOp } from '@constants/empty';
import { showTTUOption } from '@api/fetchers/ttu';
import Dynamic from '@base/Dynamic/Dynamic';
import {
  getTTUShow,
  getShowTTUCurtain,
  getTTUIsGoalEnabled
} from '@stores/selectors/ttuSelectors';
import useMyInfo from '@api/hooks/auth/useMyInfo';
import { getIsSubscribedToGoal } from '@api/selectors/subscription';
import Typography from 'aquilla/core/atoms/Typography';
import { useIsMobile } from '@stores/AppCommonData';
import { getIsLoggedIn } from '@api/selectors/auth';
import { LOGIN_DRAWER } from '@constants/toggleables';
import { useToggleable } from '@stores/toggleables';
import { CHECKOUT_SUBSCRIPTION_TYPE_SELECTED } from '../constants/segment';
import FAQSDrawer from './FAQSDrawer';
import HighlightsDrawer from './HighlightsDrawer';
import { IconicFeatures, LiteFeatures, PlusFeatures } from './Features';

import OfflineCentres from './OfflineCentres';
import NewPlanSelection from './MwebPlanSelectionRevamp/PlanSelection';

const TTURenderer = Dynamic(() => import('@layout/TTURenderer'));

const StyTTUBanner = styled(TTUBanner)`
  margin-top: ${Spacings.SPACING_10B} !important;
`;

const TTUWrapper = styled.div`
  grid-column: span 24;
  display: flex;
  ${mediaQuery.uptoMobile} {
    grid-column: span 8;
  }
`;

const OFflineCentreFlatWrapper = styled.div`
  grid-column: span 24;

  ${mediaQuery.uptoMobile} {
    grid-column: span 8;
  }
  background-color: var(--color-i-white);
  padding: ${Spacings.SPACING_6B};
  box-shadow: ${boxShadowGenerator()};
  border-radius: ${Spacings.SPACING_2B};
`;

const CardWrapper = styled.div<{
  $hasExternalApps: boolean;
  $numberOfCardsShown: number;
}>`
  grid-column: span 8;
  grid-column: ${({ $numberOfCardsShown }) => {
    if ($numberOfCardsShown === 1) return '7/18';
    if ($numberOfCardsShown === 2) return 'span 12';
    if ($numberOfCardsShown === 3) return 'span 8';
  }};
  background-color: var(--color-i-white);
  border: 1px solid var(--color-divider);
  border-radius: ${Spacings.SPACING_2B};
  position: relative;
  box-shadow: ${boxShadowGenerator()};
  height: ${({ $hasExternalApps }) => ($hasExternalApps ? '540px' : '460px')};
  height: auto;
  display: flex;
  flex-direction: column;
  justify-content: space-between;

  ${mediaQuery.uptoMobile} {
    grid-column: span 8;
    height: auto !important;
    margin-bottom: ${Spacings.SPACING_4B};
    margin-right: 0px !important;
  }
`;

const HeaderContainer = styled.div`
  text-align: center;
  grid-column: span 24;
  padding-bottom: ${Spacings.SPACING_5B};
  ${mediaQuery.uptoMobile} {
    grid-column: span 8;
    padding-bottom: ${Spacings.SPACING_3B};
  }
`;

const SubCaption = styled(Typography)`
  color: var(--color-text-secondary);
  font-weight: normal;
`;

interface PreCheckoutProps {
  externalApps: any[];
  showPlans: AnyArgVoidReturnFunction;
  goalData: {
    [key: string]: any;
  };
  isIconicUpgrade: boolean;
  subscriptionFeatures: any;
  subscriptionHighlights: any;
  iconicFAQs: any;
  isLiteAvailable: boolean;
}

const PreCheckout: FC<PreCheckoutProps> = ({
  showPlans,
  goalData,
  externalApps,
  isIconicUpgrade,
  subscriptionFeatures,
  subscriptionHighlights,
  iconicFAQs,
  isLiteAvailable
}) => {
  const router = useRouter();
  const { goalUID, source } = router.query;
  const isMobile = useIsMobile();
  const mwebPlanSelectionV2 = source === 'app' && isMobile;
  const hasExternalApps = externalApps?.length > 0;
  const [drawerState, setdrawerState] = useState(false);
  const openLearnMoreDrawer = () => {
    setdrawerState(true);
  };
  const closeLearMoreDrawer = () => setdrawerState(false);
  const [selectedPriceMode, setSelectedPriceMode] = useState();
  const { openElement: openLoginDrawer } = useToggleable(LOGIN_DRAWER);
  const isLoggedIn = useMyInfo(getIsLoggedIn);

  const [showFAQDrawer, setShowFAQDrawer] = useState(false);

  const isGoalEnabled = useTTUStore(getTTUIsGoalEnabled);
  const isGoalSubscribed = useMyInfo(getIsSubscribedToGoal(goalUID));
  const ttuShow = useTTUStore(getTTUShow);
  const showTTUCurtain = useTTUStore(getShowTTUCurtain);
  const [backButton, setBackButton] = useState({
    value: false,
    fn: noOp
  });
  const { hasOfflineCentres } = subscriptionFeatures;
  const handleBackPress = () => backButton.fn();
  const ttuOptions = {};

  const openFAQDrawer = () => {
    closeLearMoreDrawer();
    setShowFAQDrawer(true);
  };

  const plusFeatures =
    subscriptionFeatures &&
    subscriptionFeatures.features?.filter(
      (feature) => feature.isPlus && !feature.isHeader
    );

  const iconicFeatures =
    subscriptionFeatures &&
    subscriptionFeatures.features?.filter(
      (feature) => feature.isIconic && !feature.isHeader
    );

  let liteFeatures =
    subscriptionFeatures &&
    subscriptionFeatures.features?.filter(
      (feature) => feature.isLite && !feature.isHeader
    );

  if (!liteFeatures?.length) {
    liteFeatures = [
      {
        id: 1,
        name: 'Access to curated test series'
      },
      {
        id: 2,
        name: 'Daily practice questions'
      }
    ];
  }

  const eventProps = useCheckoutEventProps(goalData.uid);

  const selectBtnHandler = (priceMode) => {
    if (isLoggedIn) {
      segment.track(CHECKOUT_SUBSCRIPTION_TYPE_SELECTED, {
        ...eventProps,
        subscription_type: priceMode
      });
      showPlans(priceMode.toLowerCase());
    } else {
      setSelectedPriceMode(priceMode);
      openLoginDrawer();
    }
  };

  useEffect(() => {
    if (isLoggedIn && selectedPriceMode && selectedPriceMode !== 'offline')
      selectBtnHandler(selectedPriceMode);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn]);

  const getTTUBanner = () => {
    if (isGoalEnabled && !isGoalSubscribed)
      return <StyTTUBanner goalDetails={goalData} />;
    return null;
  };

  const onCloseTTU = () => {
    setBackButton({ value: false, fn: noOp });
    showTTUCurtain(false);
    showTTUOption(goalUID);
  };
  const isIconicCardShown = goalData?.isIconicAvailable;

  let numberOfCardsShown = 1;
  if (isLiteAvailable) numberOfCardsShown += 1;
  if (isIconicCardShown) numberOfCardsShown += 1;
  if (!subscriptionFeatures.id) return null;
  return mwebPlanSelectionV2 ? (
    <NewPlanSelection />
  ) : (
    <>
      <HeaderContainer>
        <Typography as="p" variant="h3">
          {goalData.name}
        </Typography>
        <SubCaption as="h1" variant="h5">
          Select a subscription plan that suits you
        </SubCaption>
      </HeaderContainer>

      <HighlightsDrawer
        isIconicUpgrade={isIconicUpgrade}
        drawerState={drawerState}
        openFAQDrawer={openFAQDrawer}
        closeLearMoreDrawer={closeLearMoreDrawer}
        externalApps={externalApps}
        subscriptionHighlights={subscriptionHighlights}
      />

      <FAQSDrawer
        showFAQDrawer={showFAQDrawer}
        setShowFAQDrawer={setShowFAQDrawer}
        faqs={
          iconicFAQs && iconicFAQs.faqs && iconicFAQs.faqs.length
            ? iconicFAQs.faqs
            : []
        }
      />

      {isIconicCardShown && (
        <CardWrapper
          $hasExternalApps={hasExternalApps}
          $numberOfCardsShown={numberOfCardsShown}
        >
          <IconicFeatures
            subscriptionFeatures={subscriptionFeatures}
            features={iconicFeatures}
            hasExternalApps={hasExternalApps}
            selectBtnHandler={selectBtnHandler}
            openLearnMoreDrawer={openLearnMoreDrawer}
          />
        </CardWrapper>
      )}
      <CardWrapper
        $hasExternalApps={hasExternalApps}
        $numberOfCardsShown={numberOfCardsShown}
      >
        <PlusFeatures
          subscriptionFeatures={subscriptionFeatures}
          features={plusFeatures}
          hasExternalApps={hasExternalApps}
          selectBtnHandler={selectBtnHandler}
        />
      </CardWrapper>
      {isLiteAvailable && (
        <CardWrapper
          $hasExternalApps={hasExternalApps}
          $numberOfCardsShown={numberOfCardsShown}
        >
          <LiteFeatures
            subscriptionFeatures={subscriptionFeatures}
            features={liteFeatures}
            hasExternalApps={hasExternalApps}
            selectBtnHandler={selectBtnHandler}
          />
        </CardWrapper>
      )}
      {hasOfflineCentres && (
        <OFflineCentreFlatWrapper>
          <OfflineCentres
            subscriptionFeatures={subscriptionFeatures}
            setSelectedPriceMode={setSelectedPriceMode}
            selectedPriceMode={selectedPriceMode}
          />
        </OFflineCentreFlatWrapper>
      )}
      <TTUWrapper>{getTTUBanner()}</TTUWrapper>
      <TTURenderer
        // @ts-ignore
        onCloseTTU={onCloseTTU}
        showTTU={ttuShow}
        backButton={backButton}
        setBackButton={setBackButton}
        handleBackPress={handleBackPress}
        ttuTriggerSource="Scroll Trigger"
        ttuOptions={ttuOptions}
      />
    </>
  );
};

export default PreCheckout;
